import popupBannerService from '../../api/popup-banners-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '../../util/array';
import { mapErrorsToInputs, scrollToFirstError } from '../../util/forms';

const state = {
  popupBanners: null,
  popupBannerPagination: {},
  newPopupBanner: {},
  editedPopupBanner: {},
  popupBannerValidationErrors: {},
  popupBannerFilterParams: {},
  loadingPopupBanners: false,
  savingPopupBanner: false,
};

const mutations = {
  SET_POPUP_BANNERS(state, { data, current_page, per_page, total }) {
    state.popupBanners = data;
    state.popupBannerPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.popupBannerFilterParams = params;
  },

  STORE_POPUP_BANNER(state, popupBanner) {
    state.popupBanners = [...state.popupBanners, popupBanner];
    state.popupBannerValidationErrors = {};
    state.popupBannerPagination.total += 1;
  },

  SET_EDITED_POPUP_BANNER(state, popupBanner) {
    state.popupBannerValidationErrors = {};
    state.editedPopupBanner = JSON.parse(JSON.stringify(popupBanner));
  },

  UPDATE_POPUP_BANNER(state, popupBanner) {
    state.popupBanners = updateArrayItem(state.popupBanners, popupBanner);
  },

  DELETE_POPUP_BANNER(state, popupBanner) {
    state.popupBanners = removeArrayItem(state.popupBanners, popupBanner);
    state.popupBannerPagination.total -= 1;
  },

  SET_POPUP_BANNER_VALIDATION_ERRORS(state, validationErrors) {
    state.popupBannerValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.popupBannerValidationErrors[field];
  },

  SET_LOADING_POPUP_BANNERS(state, loading) {
    state.loadingPopupBanners = loading;
  },

  SET_SAVING_POPUP_BANNER(state, saving) {
    state.savingPopupBanner = saving;
  },
};

const actions = {
  async fetchPopupBanners({ commit }, params) {
    commit('SET_LOADING_POPUP_BANNERS', true);
    return popupBannerService
      .getPage(params)
      .then((res) => {
        commit('SET_POPUP_BANNERS', res.data);
        if (params.page !== state.popupBannerFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_POPUP_BANNERS', false);
      });
  },

  async storePopupBanner({ commit }, popupBanner) {
    commit('SET_SAVING_POPUP_BANNER', true);
    return popupBannerService
      .create(popupBanner)
      .then((res) => {
        commit('STORE_POPUP_BANNER', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('popup_banner_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_POPUP_BANNER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => {
        commit('SET_SAVING_POPUP_BANNER', false);
      });
  },

  async editPopupBanner({ state, commit }, popupId) {
    const popupBanner = state.popupBanners?.find((s) => s.id === popupId);
    if (popupBanner) {
      commit('SET_EDITED_POPUP_BANNER', popupBanner);
      return Promise.resolve(popupBanner);
    }
    return popupBannerService.getById(popupId).then((res) => {
      commit('SET_EDITED_POPUP_BANNER', res.data);
    });
  },

  async updatePopupBanner({ commit }, popupBanner) {
    commit('SET_SAVING_POPUP_BANNER', true);
    return popupBannerService
      .update(popupBanner)
      .then((res) => {
        commit('UPDATE_POPUP_BANNER', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('popup_banner_updated'));
      })
      .catch((err) => {
        commit('SET_POPUP_BANNER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        scrollToFirstError();
        throw err;
      })
      .finally(() => commit('SET_SAVING_POPUP_BANNER', false));
  },

  async deletePopupBanner({ commit }, popupBanner) {
    openConfirmDialog({
      title: i18n.t('confirm_popup_banner_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      popupBannerService.delete(popupBanner).then(() => {
        commit('DELETE_POPUP_BANNER', popupBanner);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('popup_banner_deleted'));
      });
    });
  },

  clearPopupBannerValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
